/**
 * Printer profile: everything the renderer needs to know about one physical printer.
 * Stored on each printer record (server) and edited in Settings → Printers.
 */
export const PAPER_PRESETS = Object.freeze({
  '58mm': { charsPerLine: 32, dotWidth: 384 },
  '80mm': { charsPerLine: 48, dotWidth: 576 },
});

export const DEFAULT_PRINTER_PROFILE = Object.freeze({
  paperWidth: '80mm',
  charsPerLine: 48, // 42 on some 80 mm printers (Font A at 12 dots × 48 = 576)
  dotWidth: 576,
  codePage: 'cp437',
  cut: 'partial', // 'partial' | 'full' | 'none'
  drawer: Object.freeze({ enabled: false, pin: 2 }),
  density: 0, // −6…6, 0 = printer default (Epson GS ( K; ignored by printers that lack it)
  feedLines: 4,
  copies: 1,
  rasterText: false, // print lines containing ₹ / Telugu / Hindi as images
  rupeeFallback: 'Rs.',
  qrMode: 'native', // 'native' (GS ( k) | 'raster' (image; for printers without QR support) | 'off'
  printLogo: true,
});

export function profileForPaper(paperWidth, overrides = {}) {
  const preset = PAPER_PRESETS[paperWidth] ?? PAPER_PRESETS['80mm'];
  return normalizeProfile({ ...DEFAULT_PRINTER_PROFILE, paperWidth, ...preset, ...overrides });
}

/** Fill gaps with defaults and clamp numbers, so a half-filled record still prints. */
export function normalizeProfile(p = {}) {
  const preset = PAPER_PRESETS[p.paperWidth] ?? PAPER_PRESETS['80mm'];
  const n = (v, lo, hi, d) => (Number.isFinite(Number(v)) ? Math.min(hi, Math.max(lo, Math.round(Number(v)))) : d);
  return {
    ...DEFAULT_PRINTER_PROFILE,
    ...p,
    paperWidth: PAPER_PRESETS[p.paperWidth] ? p.paperWidth : '80mm',
    charsPerLine: n(p.charsPerLine, 24, 64, preset.charsPerLine),
    dotWidth: n(p.dotWidth, 256, 832, preset.dotWidth),
    density: n(p.density, -6, 6, 0),
    feedLines: n(p.feedLines, 0, 10, DEFAULT_PRINTER_PROFILE.feedLines),
    copies: n(p.copies, 1, 5, 1),
    drawer: { ...DEFAULT_PRINTER_PROFILE.drawer, ...(p.drawer ?? {}) },
  };
}
